import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Level.scss";
import { LevelGameData, TLevel } from "../../GamePlayer";
import { DNA, DNAElementValue, DNAFragmentValue } from "../../../../../components/Game/DNA/DNA";
import { clone, shuffle, unset } from "lodash";
import { DNAElement } from "../../../../../components/Game/DNA/DNAElement/DNAElement";
import { Draggable } from "../../../../../components/Draggable/Draggable";
import { Droppable } from "../../../../../components/Droppable/Droppable";
import { Portal } from "../../../../../containers/Portal/Portal";
import { useGameState } from "../../../../../hooks/useGameState";
import { LevelBlocks } from "./LevelBlocks/LevelBlocks";
import { ResultBlocks } from "./ResultBlocks/ResultBlocks";
import { LevelInputBlocks } from "./LevelInputBlocks/LevelInputBlocks";
import { UnlockSlider } from "../../../../../components/Game/UnlockSlider/UnlockSlider";
import { TimerContext } from "../../../../../context/TimerContext";

interface IProps {
    data: LevelGameData
    level: TLevel
    currentLevel: TLevel
    onFinish: () => void
}

export function Level({ data, level, currentLevel, onFinish }: IProps) {
    const { time } = useContext(TimerContext);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropRef = useRef<HTMLDivElement>(null);
    const allOptions = useMemo(() => {
        return shuffle([
            ...data.answers[0],
            ...data.answers[1],
            ...data.answers[2],
            ...(data.wrongAnswers ?? [])
        ])
    }, [data.answers, data.wrongAnswers]);
    const [choices, setChoices] = useState<[{[row: number]: number}, {[row: number]: number}, {[row: number]: number}]>([{}, {}, {}]);
    const [completed, setCompleted] = useState(false);
    const [locked, setLocked] = useState(true);
    const [startDatetime, setStartDatetime] = useState<Date|undefined>(undefined);
    const [hideForASec, setHideForASec] = useState(false);
    const { addPoints, gameState } = useGameState();

    const removeGivenAnswer = (state: [{[row: number]: number}, {[row: number]: number}, {[row: number]: number}], index: number) => {
        [0, 1, 2].forEach((foundCol) => {
            Object.keys(state[foundCol]).forEach((foundRow) => {
                const value = state[foundCol][parseInt(foundRow)];
                if (value === index) {
                    unset(state, `${foundCol}.${foundRow}`);
                }
            })
        });
        return state;
    };

    // Reset when not playing
    useEffect(() => {
        if (gameState !== 'playing') {
            setHideForASec(true);
            requestAnimationFrame(() => {
                setHideForASec(false);
            });
            setStartDatetime(undefined);
        }
    }, [gameState]);

    useEffect(() => {
        if (gameState !== 'playing') return;
        if (currentLevel === level && !startDatetime) {
            setStartDatetime(new Date());
        }
    }, [gameState, currentLevel, level, startDatetime]);

    const resetAnswer = (elm: HTMLElement) => {
        const index = parseInt(elm.dataset.answer as string);

        const newState = JSON.parse(JSON.stringify(choices));

        setChoices(removeGivenAnswer(newState, index));
    }

    const setAnswer = (columnIndex: number, rowIndex: number) => {
        return (elm: HTMLElement) => {
            const index = parseInt(elm.dataset.answer as string);

            setChoices((prevState) => {
                let newState = clone(prevState);

                // Remove index from other columns
                newState = removeGivenAnswer(newState, index);

                // Add the index
                newState[columnIndex][rowIndex] = index;
                return newState;
            })
        };
    }

    // Validate
    useEffect(() => {
        const columnValidations: [boolean, boolean, boolean] = [false, false, false];
        data.answers.forEach((answers, colIndex) => {
            const desiredOutcome = data.result[colIndex];
            const outcome: DNAElementValue = [0, 0, 0, 0];
            answers.forEach((answer, rowIndex) => {
                const choice = choices[colIndex][rowIndex];
                if (choice !== undefined) {
                    const answer = allOptions[choice]!;
                    answer.forEach((fragment, fragmentIndex) => {
                        if (fragment >= 0) {
                            outcome[fragmentIndex] = Math.min(2, outcome[fragmentIndex] + fragment) as DNAFragmentValue;
                        } else {
                            outcome[fragmentIndex] = Math.max(-2, outcome[fragmentIndex] + fragment) as DNAFragmentValue;
                        }
                    });
                }
            });

            columnValidations[colIndex] = outcome.join(', ') === desiredOutcome.join(', ');
        });
        let allValid = true;
        columnValidations.forEach((isValid) => {
            allValid = allValid && isValid;
        });

        if (allValid) {
            setCompleted(true);
        }
    }, [choices, allOptions, data.result, data.answers]);


    useEffect(() => {
        if (completed) {
            const [minutes, seconds, milliseconds] = time;
            const msLeft =
                minutes * 60 * 1000 +
                seconds * 1000 +
                milliseconds;
            const secondsLeft = Math.max(Math.round(msLeft / 100), 1);
            setLocked(false);
            setCompleted(false);
            addPoints(secondsLeft);
            setTimeout(() => {
                setLocked(true);
                onFinish();
                setChoices([{}, {}, {}]);
            }, 2500);
        }
    }, [completed, onFinish, time, level, addPoints]);

    const resetDrop = (elm: HTMLElement, event: PointerEvent) => {
        const { target } = event;
        if ((target as HTMLDivElement).classList.contains('fake-dropper')) {
            const optionsWrapper = ((target as HTMLDivElement).previousSibling as HTMLDivElement);
            const firstDroppableElement = optionsWrapper.querySelector('.is-droppable:not(:has(.draggable-wrapper))')

            firstDroppableElement!.appendChild(elm);
            resetAnswer(elm);
        }
    }

    if (hideForASec) {
        return <>..</>;
    }

    return (
        <div className={`level-wrapper ${level === currentLevel ? 'active' : ''} ${!locked ? 'completed' : ''}`} ref={wrapperRef}>
            {completed && (currentLevel === level) ? <>
                <Portal>
                    <div style={{position: 'absolute', height: '100vh', width: '100vw', textAlign: 'center'}}>
                        <h1>COMPLETED!!!</h1>
                    </div>
                </Portal>
            </> : null}
            <LevelBlocks>
                <div className="options-wrapper">
                    {allOptions.map((optionDNAElementData, index) => {
                        return <div className={"option-cell"} key={index}>
                            <Droppable onDrop={resetAnswer} shouldAppend={true}>
                                <Draggable data-answer={index}>
                                    <DNAElement value={optionDNAElementData} />
                                </Draggable>
                            </Droppable>
                        </div>;
                    })}
                    {(new Array(9 - allOptions.length)).fill(0).map((nothing, index) => {
                        return <div className={"option-cell"} key={index}>
                            <Droppable onDrop={resetAnswer} shouldAppend={true} />
                        </div>
                    })}
                </div>
                <Droppable onDrop={resetDrop} className="fake-dropper"></Droppable>
            </LevelBlocks>
            <ResultBlocks>
                <DNA value={data.result} />
            </ResultBlocks>
            <LevelInputBlocks>
                <div className="answers-wrapper" ref={dropRef}>
                    {data.answers.map((answers, index) => {
                        return <div className={"answers-column"} key={index}>
                            {answers.map((answer, rowIndex) => {
                                return <div className={"answer-cell"} key={rowIndex}><Droppable onDrop={setAnswer(index, rowIndex)} shouldAppend={true} /></div>
                            })}
                        </div>;
                    })}
                </div>
            </LevelInputBlocks>
            <UnlockSlider locked={locked} />
        </div>
    )
}
